// admin-testimonials.js - admin review panel for user-submitted testimonials.
// Loaded by admin.js for role=admin. Uses /api/testimonials/admin/* endpoints.
// A testimonial is only ever public when the submitter consented AND an admin
// approves it here, so this panel is the publish gate.

import { api } from './api.js';

const STATE = { items: [], filter: '' };

function esc(s) {
  if (s == null) return '';
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function stars(n) {
  n = parseInt(n, 10) || 0;
  if (n < 1 || n > 5) return '<span style="color:var(--text-dim,#94a3b8);">no rating</span>';
  return '<span style="color:#f59e0b;">' + '★'.repeat(n) + '</span><span style="color:var(--text-dim,#475569);">' + '☆'.repeat(5 - n) + '</span>';
}

function statusPill(s) {
  const map = {
    pending: ['#b45309', '#fffbeb'],
    approved: ['#047857', '#ecfdf5'],
    rejected: ['#b91c1c', '#fef2f2'],
  };
  const [c, bg] = map[s] || ['#475569', '#f1f5f9'];
  return `<span style="font-size:11px;font-weight:700;color:${c};background:${bg};border-radius:9999px;padding:2px 10px;">${esc(s)}</span>`;
}

function fmt(d) { if (!d) return ''; const x = new Date(d); return isNaN(x) ? '' : x.toLocaleDateString(); }

async function refresh() {
  const list = document.getElementById('admin-testi-list');
  if (!list) return;
  list.innerHTML = '<div style="padding:24px;text-align:center;color:var(--text-dim,#94a3b8);">Loading testimonials...</div>';
  try {
    const res = await api.adminTestimonials(STATE.filter);
    STATE.items = (res && (res.testimonials || res.data)) || [];
    renderList();
  } catch (e) {
    list.innerHTML = `<div style="padding:24px;text-align:center;color:#dc2626;">Could not load testimonials (${esc(e.message)}).</div>`;
  }
}

function renderList() {
  const list = document.getElementById('admin-testi-list');
  if (!list) return;
  if (!STATE.items.length) {
    list.innerHTML = '<div style="padding:24px;text-align:center;color:var(--text-dim,#94a3b8);">No testimonials' + (STATE.filter ? ' with status "' + esc(STATE.filter) + '"' : '') + ' yet.</div>';
    return;
  }
  list.innerHTML = STATE.items.map(t => {
    const consent = t.consentToPublish
      ? '<span style="color:#047857;font-weight:600;"><i class="fas fa-check"></i> consented to publish</span>'
      : '<span style="color:#b45309;font-weight:600;"><i class="fas fa-eye-slash"></i> private feedback only</span>';
    const attribution = [t.displayName, t.role, t.organization].filter(Boolean).map(esc).join(' &middot; ');
    return `
      <div style="border:1px solid var(--border,#1e3a5f);border-radius:8px;padding:14px 16px;margin-bottom:10px;background:var(--bg-card,var(--bg-panel,#0b1520));">
        <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:8px;">
          ${statusPill(t.status)}
          <span style="font-size:13px;">${stars(t.rating)}</span>
          <span style="font-size:12px;color:var(--text-dim,#94a3b8);">${esc(t.userEmail || '')}</span>
          <span style="margin-left:auto;font-size:11px;color:var(--text-dim,#94a3b8);">${fmt(t.createdAt)} &middot; ${esc(t.tier || '')}</span>
        </div>
        ${t.headline ? `<div style="font-weight:700;font-size:14px;margin-bottom:4px;">${esc(t.headline)}</div>` : ''}
        <div style="font-size:13.5px;line-height:1.55;white-space:pre-wrap;margin-bottom:8px;">${esc(t.body)}</div>
        <div style="font-size:12px;color:var(--text-dim,#94a3b8);margin-bottom:10px;">${attribution || '<em>no attribution</em>'} &nbsp;&middot;&nbsp; ${consent}</div>
        <div style="display:flex;gap:8px;flex-wrap:wrap;">
          <button class="btn btn-sm testi-approve" data-id="${esc(t.id)}" ${t.status === 'approved' ? 'disabled' : ''} style="color:#047857;border-color:#047857;"><i class="fas fa-check"></i> Approve</button>
          <button class="btn btn-sm testi-reject" data-id="${esc(t.id)}" ${t.status === 'rejected' ? 'disabled' : ''} style="color:#b91c1c;border-color:#b91c1c;"><i class="fas fa-ban"></i> Reject</button>
          <button class="btn btn-sm testi-pending" data-id="${esc(t.id)}" ${t.status === 'pending' ? 'disabled' : ''}><i class="fas fa-rotate-left"></i> Reset to pending</button>
        </div>
      </div>`;
  }).join('');
  wireRows();
}

function wireRows() {
  const list = document.getElementById('admin-testi-list');
  if (!list) return;
  const setStatus = async (id, status) => {
    try { await api.adminSetTestimonialStatus(id, status); await refresh(); }
    catch (e) { alert('Could not update: ' + (e.message || e)); }
  };
  list.querySelectorAll('.testi-approve').forEach(b => b.addEventListener('click', () => setStatus(b.dataset.id, 'approved')));
  list.querySelectorAll('.testi-reject').forEach(b => b.addEventListener('click', () => setStatus(b.dataset.id, 'rejected')));
  list.querySelectorAll('.testi-pending').forEach(b => b.addEventListener('click', () => setStatus(b.dataset.id, 'pending')));
}

export function initAdminTestimonials() {
  const filter = document.getElementById('admin-testi-filter');
  if (filter) filter.addEventListener('change', () => { STATE.filter = filter.value || ''; refresh(); });
  const btn = document.getElementById('btn-testi-refresh');
  if (btn) btn.addEventListener('click', refresh);
  // Load when the Testimonials subtab is opened (the panel is hidden at boot).
  const tab = document.querySelector('.atab[data-atab="testimonials"]');
  if (tab) tab.addEventListener('click', () => setTimeout(refresh, 50));
}

export default { initAdminTestimonials };
